import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bug, Send, CheckCircle2, Loader2, PenTool, Circle, ArrowUpRight, Undo2, Trash2, Check, Pencil } from 'lucide-react';
import { useTheme } from '../../styles/ThemeContext';
interface QAReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  screenshotUrl: string;
  onSubmit: (data: {
    category: string;
    priority: string;
    notes: string;
    annotations?: DrawingPath[];
  }) => Promise<void>;
}
const CATEGORIES = [{
  id: 'bug',
  label: 'Bug'
}, {
  id: 'feature',
  label: 'Feature'
}, {
  id: 'visual',
  label: 'Visual'
}, {
  id: 'other',
  label: 'Other'
}];
const PRIORITIES = [{
  id: 'critical',
  label: 'Critical',
  dot: 'bg-red-500'
}, {
  id: 'high',
  label: 'High',
  dot: 'bg-orange-500'
}, {
  id: 'medium',
  label: 'Medium',
  dot: 'bg-yellow-500'
}, {
  id: 'low',
  label: 'Low',
  dot: 'bg-emerald-500'
}];
const COLORS = [{
  id: 'red',
  value: '#ef4444'
}, {
  id: 'yellow',
  value: '#eab308'
}, {
  id: 'cyan',
  value: '#06b6d4'
}, {
  id: 'white',
  value: '#ffffff'
}];
type Tool = 'draw' | 'circle' | 'arrow';
type Point = {
  x: number;
  y: number;
};
export type DrawingPath = {
  type: Tool;
  color: string;
  points: Point[];
};
export function QAReportModal({
  isOpen,
  onClose,
  screenshotUrl,
  onSubmit
}: QAReportModalProps) {
  const {
    isDark
  } = useTheme();
  const [category, setCategory] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Annotation State
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [currentTool, setCurrentTool] = useState<Tool>('draw');
  const [currentColor, setCurrentColor] = useState('#ef4444');
  const [paths, setPaths] = useState<DrawingPath[]>([]);
  const [currentPath, setCurrentPath] = useState<DrawingPath | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setCategory('bug');
      setPriority('medium');
      setNotes('');
      setSubmitSuccess(false);
      setIsSubmitting(false);
      setIsAnnotating(false);
      setPaths([]);
      setCurrentPath(null);
    }
  }, [isOpen]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit({
        category,
        priority,
        notes,
        annotations: paths
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1800);
    } catch (error) {
      console.error('Failed to submit report', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Canvas Drawing Logic
  const drawPath = (ctx: CanvasRenderingContext2D, path: DrawingPath) => {
    if (path.points.length < 2) return;
    ctx.strokeStyle = path.color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (path.type === 'draw') {
      ctx.beginPath();
      ctx.moveTo(path.points[0].x, path.points[0].y);
      for (let i = 1; i < path.points.length; i++) {
        ctx.lineTo(path.points[i].x, path.points[i].y);
      }
      ctx.stroke();
    } else if (path.type === 'circle') {
      const start = path.points[0];
      const end = path.points[path.points.length - 1];
      const radiusX = Math.abs(end.x - start.x) / 2;
      const radiusY = Math.abs(end.y - start.y) / 2;
      const centerX = Math.min(start.x, end.x) + radiusX;
      const centerY = Math.min(start.y, end.y) + radiusY;
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (path.type === 'arrow') {
      const start = path.points[0];
      const end = path.points[path.points.length - 1];
      const headlen = 15;
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const angle = Math.atan2(dy, dx);
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.lineTo(end.x - headlen * Math.cos(angle - Math.PI / 6), end.y - headlen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headlen * Math.cos(angle + Math.PI / 6), end.y - headlen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    }
  };
  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    paths.forEach(p => drawPath(ctx, p));
    if (currentPath) drawPath(ctx, currentPath);
  };
  useEffect(() => {
    redrawCanvas();
  }, [paths, currentPath]);
  // Resize canvas to match container
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (canvas && container) {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
        redrawCanvas();
      }
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [isOpen, isAnnotating]);
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };
  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isAnnotating) return;
    e.preventDefault(); // Prevent scrolling on touch
    const point = getCoordinates(e);
    if (!point) return;
    setCurrentPath({
      type: currentTool,
      color: currentColor,
      points: [point]
    });
  };
  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isAnnotating || !currentPath) return;
    e.preventDefault();
    const point = getCoordinates(e);
    if (!point) return;
    setCurrentPath(prev => {
      if (!prev) return null;
      return {
        ...prev,
        points: [...prev.points, point]
      };
    });
  };
  const handlePointerUp = () => {
    if (!isAnnotating || !currentPath) return;
    setPaths(prev => [...prev, currentPath]);
    setCurrentPath(null);
  };
  const handleUndo = () => {
    setPaths(prev => prev.slice(0, -1));
  };
  const handleClear = () => {
    setPaths([]);
  };
  return <AnimatePresence data-id="element-1621">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-1622">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-1623" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`relative w-full max-w-[460px] max-h-[90vh] rounded-3xl shadow-2xl overflow-y-auto border custom-scrollbar ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-1624">
            {/* Close Button */}
            {!isAnnotating && <button onClick={onClose} className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-colors ${isDark ? 'bg-black/20 hover:bg-black/40 text-white/70 hover:text-white' : 'bg-white/50 hover:bg-white/80 text-gray-500 hover:text-gray-900'}`} data-id="element-1625">
                <X className="w-5 h-5" data-id="element-1626" />
              </button>}

            <div className="p-6" data-id="element-1627">
              {/* Screenshot Area */}
              <div className="mb-5" data-id="element-1628">
                <div ref={containerRef} className={`relative w-full aspect-[16/10] rounded-2xl overflow-hidden bg-black/10 ${isAnnotating ? 'ring-2 ring-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)]' : ''}`} data-id="element-1629">
                  <img src={screenshotUrl} alt="Screenshot" className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-id="element-1630" />

                  <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full ${isAnnotating ? 'cursor-crosshair z-10 touch-none' : 'pointer-events-none z-10'}`} onMouseDown={handlePointerDown} onMouseMove={handlePointerMove} onMouseUp={handlePointerUp} onMouseLeave={handlePointerUp} onTouchStart={handlePointerDown} onTouchMove={handlePointerMove} onTouchEnd={handlePointerUp} data-id="element-1631" />

                  {!isAnnotating && <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider z-20 ${isDark ? 'bg-black/50 backdrop-blur-md text-white/80' : 'bg-black/40 backdrop-blur-md text-white'}`} data-id="element-1632">
                      Screenshot attached
                    </div>}

                  {!isAnnotating && <button onClick={() => setIsAnnotating(true)} className={`absolute bottom-3 right-3 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-transform hover:scale-105 z-20 shadow-lg ${isDark ? 'bg-red-500 text-white' : 'bg-red-600 text-white'}`} data-id="element-1633">
                      <Pencil className="w-3.5 h-3.5" data-id="element-1634" />
                      {paths.length > 0 ? 'Edit Markup' : 'Annotate'}
                    </button>}
                </div>

                {/* Annotation Toolbar — below the image */}
                <AnimatePresence data-id="element-1635">
                  {isAnnotating && <motion.div initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} className={`mt-3 flex items-center justify-between p-2 rounded-xl backdrop-blur-xl shadow-lg border ${isDark ? 'bg-slate-800/90 border-white/10' : 'bg-white/90 border-gray-200'}`} data-id="element-1636">
                      <div className="flex items-center gap-1" data-id="element-1637">
                        <button onClick={() => setCurrentTool('draw')} className={`p-2 rounded-lg transition-colors ${currentTool === 'draw' ? isDark ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-1638">
                          <PenTool className="w-4 h-4" data-id="element-1639" />
                        </button>
                        <button onClick={() => setCurrentTool('circle')} className={`p-2 rounded-lg transition-colors ${currentTool === 'circle' ? isDark ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-1640">
                          <Circle className="w-4 h-4" data-id="element-1641" />
                        </button>
                        <button onClick={() => setCurrentTool('arrow')} className={`p-2 rounded-lg transition-colors ${currentTool === 'arrow' ? isDark ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-900' : isDark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`} data-id="element-1642">
                          <ArrowUpRight className="w-4 h-4" data-id="element-1643" />
                        </button>
                        <div className={`w-px h-6 mx-1 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-1644" />
                        <div className="flex items-center gap-1 px-1" data-id="element-1645">
                          {COLORS.map(c => <button key={c.id} onClick={() => setCurrentColor(c.value)} className={`w-5 h-5 rounded-full transition-transform ${currentColor === c.value ? 'scale-125 ring-2 ring-offset-1 ring-offset-transparent' : 'hover:scale-110'}`} style={{
                      backgroundColor: c.value,
                      ringColor: c.value
                    }} data-id="element-1646" />)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1" data-id="element-1647">
                        <button onClick={handleUndo} disabled={paths.length === 0} className={`p-2 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-white hover:bg-white/10 disabled:opacity-30' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-30'}`} data-id="element-1648">
                          <Undo2 className="w-4 h-4" data-id="element-1649" />
                        </button>
                        <button onClick={handleClear} disabled={paths.length === 0} className={`p-2 rounded-lg transition-colors ${isDark ? 'text-white/50 hover:text-red-400 hover:bg-white/10 disabled:opacity-30' : 'text-gray-500 hover:text-red-600 hover:bg-gray-100 disabled:opacity-30'}`} data-id="element-1650">
                          <Trash2 className="w-4 h-4" data-id="element-1651" />
                        </button>
                        <button onClick={() => setIsAnnotating(false)} className={`ml-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${isDark ? 'bg-white text-black hover:bg-white/90' : 'bg-gray-900 text-white hover:bg-gray-800'}`} data-id="element-1652">
                          Done
                        </button>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Form Content */}
              <motion.div animate={{
            opacity: isAnnotating ? 0.3 : 1,
            pointerEvents: isAnnotating ? 'none' : 'auto'
          }} transition={{
            duration: 0.2
          }} data-id="element-1653">
                {/* Header */}
                <div className="flex items-center gap-2 mb-5" data-id="element-1654">
                  <div className={`p-1.5 rounded-lg ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`} data-id="element-1655">
                    <Bug className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-600'}`} data-id="element-1656" />
                  </div>
                  <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1657">
                    QA Report
                  </h3>
                </div>

                <AnimatePresence mode="wait" data-id="element-1658">
                  {!submitSuccess ? <motion.form key="form" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0,
                y: -10
              }} onSubmit={handleSubmit} className="space-y-5" data-id="element-1659">
                      {/* Category */}
                      <div className="space-y-2" data-id="element-1660">
                        <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-1661">
                          Category
                        </label>
                        <div className="grid grid-cols-4 gap-2" data-id="element-1662">
                          {CATEGORIES.map(cat => <button key={cat.id} type="button" onClick={() => setCategory(cat.id)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${category === cat.id ? isDark ? 'bg-white text-slate-900 border-white shadow-md' : 'bg-gray-900 text-white border-gray-900 shadow-md' : isDark ? 'bg-white/5 text-slate-400 border-white/10 hover:bg-white/10 hover:text-white' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-1663">
                              {cat.label}
                            </button>)}
                        </div>
                      </div>

                      {/* Priority */}
                      <div className="space-y-2" data-id="element-1664">
                        <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-1665">
                          Priority
                        </label>
                        <div className="grid grid-cols-4 gap-2" data-id="element-1666">
                          {PRIORITIES.map(pri => <button key={pri.id} type="button" onClick={() => setPriority(pri.id)} className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all border flex items-center justify-center gap-1.5 ${priority === pri.id ? isDark ? 'bg-white/10 text-white border-white/30 shadow-md' : 'bg-gray-100 text-gray-900 border-gray-300 shadow-md' : isDark ? 'bg-white/5 text-slate-500 border-white/10 hover:bg-white/10 hover:text-slate-300' : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100 hover:text-gray-700'}`} data-id="element-1667">
                              <div className={`w-1.5 h-1.5 rounded-full ${pri.dot}`} data-id="element-1668" />
                              {pri.label}
                            </button>)}
                        </div>
                      </div>

                      {/* Notes */}
                      <div className="space-y-2" data-id="element-1669">
                        <label className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-1670">
                          Notes
                        </label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe the issue, steps to reproduce..." rows={3} className={`w-full px-4 py-3 rounded-xl border focus:outline-none transition-all resize-none text-sm custom-scrollbar ${isDark ? 'bg-slate-800/50 border-white/10 text-white placeholder-slate-500 focus:border-red-400 focus:bg-slate-800' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-red-500 focus:bg-white'}`} required data-id="element-1671" />
                      </div>

                      {/* Submit */}
                      <button type="submit" disabled={isSubmitting || !notes.trim()} className={`w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98] ${isDark ? 'bg-red-500 hover:bg-red-400 text-white shadow-[0_0_20px_rgba(239,68,68,0.2)] disabled:opacity-50' : 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/20 disabled:opacity-50'}`} data-id="element-1672">
                        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-1673" /> : <>
                            <Send className="w-4 h-4" data-id="element-1674" />
                            Send Report
                          </>}
                      </button>
                    </motion.form> : <motion.div key="success" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex flex-col items-center py-6" data-id="element-1675">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  damping: 15,
                  stiffness: 200
                }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`} data-id="element-1676">
                        <CheckCircle2 className="w-8 h-8" data-id="element-1677" />
                      </motion.div>
                      <h3 className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1678">
                        Report sent!
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`} data-id="element-1679">
                        Thanks — the team will take a look.
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}